import { useCallback, useEffect, useRef, useState } from "react";

import { useDispatch } from "react-redux";

import {
  parseErrorResponse,
  useRequestWithFeedback,
  type OverrideRunProps,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { API as RecipientsAPI } from "../recipients/api";
import { actions as recipientActions } from "../recipients/slice";

import { API } from "./api";
import {
  isInvoiceReady,
  isInvoiceRenderable,
  isInvoiceTerminal,
} from "./helpers";

import type { Invoice } from "./types";

const DEFAULT_POLL_INTERVAL_MS = 1500;
// Safety cap so a stuck invoice can't poll forever. Sized against the backend's
// per-phase processing leases (rendering 60s, analyzing 120s, arbitrating 300s,
// matching_recipient 60s)
const DEFAULT_POLL_TIMEOUT_MS = 300000;

export const useFetchInvoice = () => {
  const { send } = useRequestWithFeedback<Invoice>();

  const fetchInvoice = useCallback(
    (uuid: string, overrideRunProps: OverrideRunProps<Invoice> = {}) => {
      return send({
        action: API.fetchInvoice(uuid),
        ...overrideRunProps,
      });
    },
    [send],
  );

  return useObjectMemo({ fetchInvoice });
};

type PollOptions = {
  intervalMs?: number;
  timeoutMs?: number;
  // fired on every successful poll, so the UI can render page previews as soon as they appear
  onUpdate?: (invoice: Invoice) => void;
  onReady?: (invoice: Invoice) => void;
};

// polls GET /v1/invoices/<uuid>/ until the pipeline reaches a terminal status
// (ready / extraction_failed) or the timeout elapses
export const usePollInvoice = () => {
  const { fetchInvoice } = useFetchInvoice();
  const [invoice, setInvoiceState] = useState<Invoice | null>(null);
  const [polling, setPolling] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // each poll run gets a fresh id. a late in-flight fetch from a superseded run
  // (e.g. the user removed the file, or started a new upload) is ignored by
  // comparing against the active id
  const runIdRef = useRef(0);
  const latestInvoiceRef = useRef<Invoice | null>(null);

  const setInvoice = useCallback((next: Invoice | null) => {
    latestInvoiceRef.current = next;
    setInvoiceState(next);
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const stop = useCallback(() => {
    runIdRef.current += 1;
    clearTimer();
    setPolling(false);
  }, [clearTimer]);

  // ensure an in-flight poll can't outlive the component
  useEffect(() => stop, [stop]);

  const poll = useCallback(
    (uuid: string, options: PollOptions = {}): Promise<Invoice | null> => {
      const {
        intervalMs = DEFAULT_POLL_INTERVAL_MS,
        timeoutMs = DEFAULT_POLL_TIMEOUT_MS,
        onUpdate,
        onReady,
      } = options;

      clearTimer();
      runIdRef.current += 1;
      const runId = runIdRef.current;
      const isStale = () => runIdRef.current !== runId;

      setPolling(true);
      const deadline = Date.now() + timeoutMs;

      return new Promise<Invoice | null>((resolve) => {
        const settle = (value: Invoice | null) => {
          clearTimer();
          setPolling(false);
          resolve(value);
        };

        const tick = () => {
          if (isStale()) {
            resolve(null);
            return;
          }
          fetchInvoice(uuid, {
            onSuccess: (data) => {
              if (isStale()) {
                resolve(null);
                return;
              }
              setInvoice(data);
              onUpdate?.(data);
              if (isInvoiceTerminal(data)) {
                if (isInvoiceReady(data)) {
                  onReady?.(data);
                }
                settle(data);
                return;
              }
              if (Date.now() >= deadline) {
                settle(data);
                return;
              }
              timerRef.current = setTimeout(tick, intervalMs);
            },
            onError: () => {
              // keep polling until the deadline rather than failing the whole flow
              if (isStale()) {
                resolve(null);
                return;
              }
              if (Date.now() >= deadline) {
                settle(latestInvoiceRef.current);
                return;
              }
              timerRef.current = setTimeout(tick, intervalMs);
            },
          });
        };
        tick();
      });
    },
    [fetchInvoice, setInvoice, clearTimer],
  );

  return useObjectMemo({
    poll,
    stop,
    polling,
    invoice,
    ready: isInvoiceReady(invoice),
    renderable: isInvoiceRenderable(invoice),
  });
};

// defaults for creating a new recipient thru invoice pay
const INVOICE_RECIPIENT_TYPE: API.Recipient["type"] = "business";
const INVOICE_RECIPIENT_ACCOUNT_TYPE: API.ACHDestination["account_type"] =
  "checking";
const INVOICE_RECIPIENT_ACCOUNT_PURPOSE: API.ACHDestination["account_purpose"] =
  "business";

type InvoiceRecipientDetails = {
  name: string;
  accountNumber: string;
  routingNumber: string;
};

type InvoiceRecipientCallbacks = {
  onSuccess?: (recipient: API.Recipient) => void;
  onError?: (errorMessage?: string) => void;
};

export const getFormErrorMessage = (
  errors: Awaited<ReturnType<typeof parseErrorResponse>>["errors"],
): string | undefined => {
  const [firstError] = errors;

  return String(firstError?.code) === "formError"
    ? firstError?.description
    : undefined;
};

// saves a recipient that was detected on an invoice but doesn't exist yet
export const useCreateRecipientFromInvoice = () => {
  const dispatch = useDispatch();
  const {
    send: sendProfile,
    loading: profileLoading,
    errorMessage: profileErrorMessage,
  } = useRequestWithFeedback<
    API.CreateRecipientProfile.Response,
    API.CreateRecipientProfile.Error
  >();
  const {
    send: sendACHDestination,
    loading: achLoading,
    errorMessage: achErrorMessage,
  } = useRequestWithFeedback<
    API.CreateUpdateACHDestination.Response,
    API.CreateUpdateACHDestination.Error
  >();

  const createACHDestination = useCallback(
    (
      recipient: API.Recipient,
      { accountNumber, routingNumber }: InvoiceRecipientDetails,
      { onSuccess, onError }: InvoiceRecipientCallbacks,
    ) =>
      sendACHDestination({
        action: RecipientsAPI.createOrUpdateACHDestination({
          account_number: accountNumber,
          routing_number: routingNumber,
          account_type: INVOICE_RECIPIENT_ACCOUNT_TYPE,
          account_purpose: INVOICE_RECIPIENT_ACCOUNT_PURPOSE,
          recipient: recipient.id,
        }),
        onData: (achDestination) => {
          dispatch(
            recipientActions.updateOne({
              ...recipient,
              ach_destination: achDestination,
            }),
          );
        },
        onSuccess: (achDestination) => {
          onSuccess?.({ ...recipient, ach_destination: achDestination });
        },
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
          onError?.(getFormErrorMessage(errors));
        },
      }),
    [dispatch, sendACHDestination],
  );

  const createRecipientFromInvoice = useCallback(
    (
      details: InvoiceRecipientDetails,
      callbacks: InvoiceRecipientCallbacks = {},
    ) =>
      sendProfile({
        action: RecipientsAPI.createOrUpdateRecipientProfile({
          name: details.name,
          type: INVOICE_RECIPIENT_TYPE,
        }),
        onData: (recipient) => {
          dispatch(recipientActions.setOne(recipient));
        },
        onSuccess: (recipient) => {
          createACHDestination(recipient, details, callbacks);
        },
        onError: async (error, setErrorMessage) => {
          const { errors } = await parseErrorResponse(error);
          if (errors.length) {
            setErrorMessage(`${errors[0].description}`);
          }
          callbacks.onError?.(getFormErrorMessage(errors));
        },
      }),
    [dispatch, sendProfile, createACHDestination],
  );

  return useObjectMemo({
    createRecipientFromInvoice,
    loading: profileLoading || achLoading,
    errorMessage: achErrorMessage || profileErrorMessage,
  });
};
